import { GridApi, ICellRendererParams } from "ag-grid-community";
import { FC, useEffect, useState } from "react";
import { Account, FavoriteEventDetail } from "types/portfolio";
import styles from "./AccountIdRenderer.module.scss";
import { Favorite } from "@mui/icons-material";
import { Rating } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { useAxios } from "myAxios";
import { dispatchCustomEvent } from "utils";
import { favoriteCallback } from "../GridViewFunction/Favorite";
import useFilterModelHighlightHTML from "components/app/useFilterModelHighlightHTML";
import { classHOC } from "components/app/ClassWrapper";
import { isFlag, FlagEnum } from "components/portfolio/utils";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AIButton from "components/common/AIButton";
// import MenuItemFavorite from "./MenuItemFavorite";

const StyledRating = withStyles({
  iconFilled: {
    color: "#B3000E",
  },
  iconHover: {
    color: "#B3000E",
  },
})(Rating);

const AccountIdRenderer: FC<ICellRendererParams & { hideFavorite?: boolean }> = ({
  node,
  api,
  data,
  hideFavorite = false,
}) => {
  if (node.rowPinned) return null;
  const isNew = data?.new;
  const isInactive = data?.accountStatus === "Inactive";

  const comonData = data;
  const markConfig: Array<{
    id: string;
    label: string;
    enable: (data: Account) => boolean;
  }> = [
    {
      id: "escalation",
      label: "Escalation Engineering",
      enable: (data) => isFlag(data?.bannerFlag, FlagEnum.Escalation),
    },
  ];
  const enabledConfig = markConfig.filter((c) => c.enable(comonData));

  return (
    <>
      <div className={styles.container_main}>
        {hideFavorite ? null : (
          <div className={styles.favorite_icon_container}>
            <FavoriteIcon
              favorite={data?.favorite}
              accountKey={data?.accountKey}
              api={api}
            />
          </div>
        )}
        <AIButton account={data} />
        <span
          style={{ fontSize: "inherit" }}
          dangerouslySetInnerHTML={{
            __html: useFilterModelHighlightHTML(api, data?.accountId),
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          lineHeight: "1rem",
          bottom: "-2px",
          left: "15px",
          paddingRight: "25px",
          width: "100%",
          display: "flex",
        }}
      >
        {isNew || isInactive ? (
          <div
            style={{
              padding: "0 8px",
              backgroundColor: isNew ? "#b0f8b4" : "#ffe1d9",
              color: isNew ? "#24ab31" : "#ff5f58",
              flex: 1,
              textAlign: "center",
              lineHeight: "14px",
              fontSize: "9px",
            }}
          >
            {isNew ? "New" : "Inactive"}
          </div>
        ) : null}
        {enabledConfig.map((x, i) => {
          return (
            <div
              key={x.id}
              className={`${styles.newMarker} ${styles[x.id]}`}
              style={{
                flex: 1,
              }}
            >
              {x.label}
            </div>
          );
        })}
      </div>
    </>
  );
};

interface FavoriteIconProps {
  favorite?: boolean;
  accountKey?: string;
  api?: GridApi;
}

export const FavoriteIcon: FC<FavoriteIconProps> = ({ favorite, accountKey, api }) => {
  const [isFavorite, setIsFavorite] = useState(favorite ? 1 : 0);
  const axios = useAxios();
  useEffect(() => {
    const handleEvent = (e: any) => {
      const detail: FavoriteEventDetail = e.detail;
      if (detail.payload.some((key) => key === accountKey)) {
        setIsFavorite(detail.type === "ADD" ? 1 : 0);
      }
    };
    addEventListener("FAVORITE_CHANGE", handleEvent);
    return () => {
      removeEventListener("FAVORITE_CHANGE", handleEvent);
    };
  }, []);
  const handleIconClick = () => {
    if (isFavorite) {
      axios.delete(`/api/portfolio/favorite/${accountKey}`).then(() => {
        setIsFavorite(0);
        dispatchCustomEvent("FAVORITE_CHANGE", {
          type: "REMOVE",
          payload: [accountKey],
        });
        favoriteCallback(api);
      });
    } else {
      axios.post(`/api/portfolio/favorite/${accountKey}`).then(() => {
        setIsFavorite(1);
        dispatchCustomEvent("FAVORITE_CHANGE", {
          type: "ADD",
          payload: [accountKey],
        });
      });
    }
  };

  return (
    <div title={isFavorite ? "Remove account from favorite" : "Add account to favorite"}>
      <StyledRating
        precision={1}
        max={1}
        icon={<Favorite style={{ fontSize: "inherit" }} />}
        emptyIcon={<FavoriteBorderIcon style={{ fontSize: "inherit" }} />}
        onChange={handleIconClick}
        value={isFavorite}
      />
    </div>
  );
};

export default classHOC(AccountIdRenderer);
